import { api } from "@breakout/backend/convex/_generated/api"
import type { Id } from "@breakout/backend/convex/_generated/dataModel"
import { formatUsdc, validateOdds } from "@breakout/sdk"
import { BINARY_OUTCOME_LABELS, getBinaryMarketTitle } from "@breakout/shared/constants"
import { formatDateTimeMs } from "@breakout/shared/utils/time"
import { cn } from "@breakout/ui/utils"
import { convexQuery } from "@convex-dev/react-query"
import { createId } from "@paralleldrive/cuid2"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useAction } from "convex/react"
import { ChevronDown, ChevronUp, RefreshCw, Upload, X } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useAccount } from "wagmi"
import { useAdminAuth } from "../hooks/useAdminAuth"
import { useLaunchWeek } from "../hooks/useLaunchWeek"
import { useAdminConfig } from "../lib/providers"
import { CUSTOM_QUESTION_CONFIG, WHITELISTED_HANDLES } from "./launch/-config"
import type { KaitoEntry, LaunchFormState } from "./launch/-types"
import { getDefaultFormTimes, toLocalDatetimeInput } from "./launch/-utils"

export const Route = createFileRoute("/launch")({
	component: LaunchPage,
})

// Constants - defined outside component to avoid recreation
const TIME_WINDOWS = ["7d", "30d", "all_time"] as const
const DURATION_PRESETS = [
	{ label: "7 days", seconds: 7 * 24 * 60 * 60 },
	{ label: "14 days", seconds: 14 * 24 * 60 * 60 },
	{ label: "30 days", seconds: 30 * 24 * 60 * 60 },
] as const

function generateRandomWeekId(): string {
	return createId()
}

function formatTimeWindowLabel(tw: (typeof TIME_WINDOWS)[number]): string {
	return tw === "all_time" ? "All" : tw.toUpperCase()
}

function LaunchPage() {
	const { isConnected, address } = useAccount()
	const { password: adminPassword } = useAdminAuth()
	useAdminConfig()

	// Form state
	const defaultTimes = getDefaultFormTimes()
	const [formState, setFormState] = useState<LaunchFormState>(() => ({
		weekId: generateRandomWeekId(),
		binaryReserveUsd: "150", // ~$150 reserve per market = b ~$216
		featuredReserveUsd: "250", // ~$250 reserve for custom question
		feeBps: "100",
		binaryOdds: new Map(),
		featuredOdds: undefined,
		...defaultTimes,
	}))
	const [showOddsConfig, setShowOddsConfig] = useState(false)

	// Snapshot state
	const [selectedTimeWindow, setSelectedTimeWindow] = useState<(typeof TIME_WINDOWS)[number]>("7d")
	const [selectedSnapshotId, setSelectedSnapshotId] = useState<string>("")

	// Actions
	const refreshKaito = useAction(api.domains.admin.actions.refreshLeaderboard)
	const uploadSnapshotToIpfs = useAction(api.domains.admin.actions.uploadSnapshotToIpfs)
	const importKaito = useAction(api.domains.admin.actions.importLeaderboard)
	const importKaitoCsv = useAction(api.domains.admin.actions.importLeaderboardCsv)
	const getCsvTemplate = useAction(api.domains.admin.actions.getLeaderboardCsvTemplate)

	// Loading states
	const [isRefreshing, setIsRefreshing] = useState(false)
	const [isUploadingIpfs, setIsUploadingIpfs] = useState(false)
	const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null)

	// Import modal state
	const [showImportModal, setShowImportModal] = useState(false)
	const [importMode, setImportMode] = useState<"csv" | "json">("csv")
	const [importCsv, setImportCsv] = useState("")
	const [importJson, setImportJson] = useState("")
	const [isImporting, setIsImporting] = useState(false)

	// Query snapshots
	const { data: snapshots } = useQuery(
		convexQuery(api.domains.admin.resolution.listSnapshots, { limit: 100 }),
	)

	// Auto-select first snapshot
	useEffect(() => {
		const firstSnapshot = snapshots?.[0]
		if (firstSnapshot && !selectedSnapshotId) {
			setSelectedSnapshotId(firstSnapshot.id as string)
		}
	}, [snapshots, selectedSnapshotId])

	// Get entities for selected snapshot
	const { data: snapshotEntities } = useQuery({
		...convexQuery(api.domains.leaderboard.queries.getSnapshotEntities, {
			snapshotId: selectedSnapshotId ? (selectedSnapshotId as Id<"snapshots">) : undefined,
		}),
		enabled: !!selectedSnapshotId,
	})

	// Derived state
	const selectedSnapshotDetails = useMemo(() => {
		if (!selectedSnapshotId || !snapshots) {
			return null
		}
		return snapshots.find(s => s.id === selectedSnapshotId) ?? null
	}, [selectedSnapshotId, snapshots])

	const entries: KaitoEntry[] = useMemo(() => {
		if (!snapshotEntities) {
			return []
		}
		return snapshotEntities
			.filter(e => WHITELISTED_HANDLES.has(e.handle.toLowerCase()))
			.map(e => ({
				entityKey: e.entityKey,
				rank: e.rank,
				displayName: e.displayName,
				handle: e.handle,
				avatarUrl: e.avatarUrl,
				mindsharePct: e.mindsharePct,
			}))
	}, [snapshotEntities])

	const tradingDuration = useMemo(() => {
		const openMs = new Date(formState.openTimeSec).getTime()
		const cutoffMs = new Date(formState.cutoffTimeSec).getTime()
		const durationMs = cutoffMs - openMs

		if (durationMs <= 0 || Number.isNaN(durationMs)) {
			return { valid: false as const }
		}

		const days = Math.floor(durationMs / (1000 * 60 * 60 * 24))
		const hours = Math.floor((durationMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
		const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))

		const parts: string[] = []
		if (days > 0) {
			parts.push(`${days}d`)
		}
		if (hours > 0) {
			parts.push(`${hours}h`)
		}
		if (minutes > 0 && days === 0) {
			parts.push(`${minutes}m`)
		}

		return { valid: true as const, label: parts.join(" ") }
	}, [formState.openTimeSec, formState.cutoffTimeSec])

	// Launch hook
	const {
		launch,
		isLaunching,
		launchProgress,
		reserves,
		liquidityParams,
		slippagePreview,
		balanceInfo,
		refetchBalance,
		oddsValidation,
	} = useLaunchWeek({
		formState,
		snapshotDetails: selectedSnapshotDetails
			? {
					id: selectedSnapshotDetails.id,
					ipfsCid: selectedSnapshotDetails.ipfsCid,
					entityCount: selectedSnapshotDetails.entityCount,
					capturedAtMs: selectedSnapshotDetails.capturedAtMs,
					provider: selectedSnapshotDetails.provider,
				}
			: null,
		entries,
	})

	// Toast helper
	const showToast = (message: string, type: "success" | "error") => {
		setToast({ message, type })
		setTimeout(() => setToast(null), 5000)
	}

	// Handlers
	async function handleRefresh() {
		if (!adminPassword) {
			return
		}
		setIsRefreshing(true)
		try {
			const result = await refreshKaito({ timeWindow: selectedTimeWindow, adminPassword })
			setSelectedSnapshotId(result.snapshotId)
			showToast(`Fetched ${result.entityCount} creators (${result.timeWindow})`, "success")
		} catch (error) {
			showToast(error instanceof Error ? error.message : "Failed to refresh", "error")
		} finally {
			setIsRefreshing(false)
		}
	}

	async function handleUploadIpfs() {
		if (!selectedSnapshotDetails || !adminPassword) {
			return
		}
		setIsUploadingIpfs(true)
		try {
			const result = await uploadSnapshotToIpfs({
				snapshotId: selectedSnapshotDetails.id,
				adminPassword,
			})
			if ("error" in result) {
				showToast(`Upload failed: ${result.error}`, "error")
			} else {
				showToast(`Uploaded to IPFS: ${result.cid}`, "success")
			}
		} catch (err) {
			showToast(err instanceof Error ? err.message : "Upload failed", "error")
		} finally {
			setIsUploadingIpfs(false)
		}
	}

	async function handleLaunch() {
		const result = await launch()
		if (result.success) {
			showToast(
				`Launched ${result.marketCount} markets! TX: ${result.txHash.slice(0, 10)}...`,
				"success",
			)
		} else {
			showToast(result.error, "error")
		}
	}

	async function handleImportKaito() {
		if (!adminPassword) {
			return
		}
		setIsImporting(true)
		try {
			if (importMode === "csv") {
				const result = await importKaitoCsv({
					adminPassword,
					csvContent: importCsv,
					timeWindow: selectedTimeWindow,
				})
				setSelectedSnapshotId(result.snapshotId)
				showToast(`Imported ${result.entityCount} creators from CSV`, "success")
				setImportCsv("")
			} else {
				const parsedEntries = JSON.parse(importJson)
				if (!Array.isArray(parsedEntries)) {
					throw new Error("Expected an array of entries")
				}
				const result = await importKaito({
					adminPassword,
					entries: parsedEntries,
					timeWindow: selectedTimeWindow,
				})
				setSelectedSnapshotId(result.snapshotId)
				showToast(`Imported ${result.entityCount} creators from JSON`, "success")
				setImportJson("")
			}
			setShowImportModal(false)
		} catch (error) {
			showToast(error instanceof Error ? error.message : "Failed to import", "error")
		} finally {
			setIsImporting(false)
		}
	}

	async function handleDownloadCsvTemplate() {
		if (!adminPassword) {
			return
		}
		try {
			const template = await getCsvTemplate({ adminPassword })
			const blob = new Blob([template], { type: "text/csv" })
			const url = URL.createObjectURL(blob)
			const a = document.createElement("a")
			a.href = url
			a.download = "leaderboard-template.csv"
			document.body.appendChild(a)
			a.click()
			document.body.removeChild(a)
			URL.revokeObjectURL(url)
		} catch (error) {
			showToast(error instanceof Error ? error.message : "Failed to download template", "error")
		}
	}

	function handleCsvFileUpload(event: React.ChangeEvent<HTMLInputElement>) {
		const file = event.target.files?.[0]
		if (!file) {
			return
		}
		const reader = new FileReader()
		reader.onload = e => setImportCsv(e.target?.result as string)
		reader.readAsText(file)
	}

	function handleDurationPreset(seconds: number) {
		const openDate = new Date(formState.openTimeSec)
		const cutoffDate = new Date(openDate.getTime() + seconds * 1000)
		setFormState(p => ({ ...p, cutoffTimeSec: toLocalDatetimeInput(cutoffDate) }))
	}

	if (!isConnected) {
		return (
			<div className="flex flex-col items-center justify-center py-20">
				<h1 className="mb-4 font-bold text-3xl">Launch Markets</h1>
				<p className="text-gray-400">Connect your wallet to launch markets.</p>
			</div>
		)
	}

	return (
		<div className="space-y-8">
			{/* Toast */}
			{toast && (
				<div
					className={`fixed top-4 right-4 z-[var(--z-toast)] rounded-lg px-4 py-3 text-sm shadow-lg ${
						toast.type === "success" ? "bg-green-600 text-white" : "bg-red-600 text-white"
					}`}
					role="alert"
				>
					{toast.message}
				</div>
			)}

			{/* Import Modal */}
			{showImportModal && (
				<div className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center bg-black/70">
					<div className="w-full max-w-2xl rounded-lg border border-gray-700 bg-gray-900 p-6">
						<div className="mb-4 flex items-center justify-between">
							<h2 className="font-semibold text-lg">Import Leaderboard Data</h2>
							<button
								aria-label="Close import modal"
								className="text-gray-400 hover:text-white"
								onClick={() => setShowImportModal(false)}
								type="button"
							>
								<X aria-hidden="true" className="size-5" />
							</button>
						</div>

						<p className="mb-4 text-gray-400 text-sm">
							Import leaderboard data from CSV or JSON when automatic scraping fails.
						</p>

						{/* Import Mode Tabs */}
						<div className="mb-4 flex gap-2">
							{(["csv", "json"] as const).map(mode => (
								<button
									className={`rounded-lg px-4 py-2 font-semibold text-sm ${
										importMode === mode
											? "bg-gray-600 text-white"
											: "border border-gray-600 text-gray-400 hover:bg-gray-800"
									}`}
									key={mode}
									onClick={() => setImportMode(mode)}
									type="button"
								>
									{mode.toUpperCase()} Import
								</button>
							))}
						</div>

						{importMode === "csv" ? (
							<>
								<div className="mb-4 rounded-lg border border-green-800 bg-green-900/30 p-3 text-green-400 text-sm">
									<p className="font-medium">CSV Import (Recommended)</p>
									<p className="mt-1 text-green-500">
										Columns: rank, handle, displayName, avatarUrl, mindsharePct
									</p>
								</div>
								<div className="mb-4 flex gap-2">
									<button
										className="rounded-lg border border-green-600 px-4 py-2 font-semibold text-green-400 text-sm hover:bg-green-900/30"
										onClick={() => handleDownloadCsvTemplate().catch(() => {})}
										type="button"
									>
										Download Template
									</button>
									<label className="cursor-pointer rounded-lg border border-gray-600 px-4 py-2 font-semibold text-gray-400 text-sm hover:bg-gray-800">
										Upload CSV
										<input
											accept=".csv"
											className="sr-only"
											onChange={handleCsvFileUpload}
											type="file"
										/>
									</label>
								</div>
								<textarea
									aria-label="CSV content"
									className="mb-4 h-48 w-full rounded-lg border border-gray-700 bg-gray-800 p-3 font-mono text-sm text-white"
									onChange={e => setImportCsv(e.target.value)}
									placeholder="rank,handle,displayName,avatarUrl,mindsharePct&#10;1,VitalikButerin,vitalik.eth,https://...,2.14"
									value={importCsv}
								/>
							</>
						) : (
							<>
								<div className="mb-4 rounded-lg border border-blue-800 bg-blue-900/30 p-3 text-blue-400 text-sm">
									<p className="font-medium">JSON Import</p>
									<p className="mt-1 text-blue-500">
										Array of objects with: rank, handle, displayName, avatarUrl?, mindsharePct?
									</p>
								</div>
								<textarea
									aria-label="JSON entries"
									className="mb-4 h-48 w-full rounded-lg border border-gray-700 bg-gray-800 p-3 font-mono text-sm text-white"
									onChange={e => setImportJson(e.target.value)}
									placeholder='[{"rank": 1, "handle": "VitalikButerin", "displayName": "vitalik.eth"}]'
									value={importJson}
								/>
							</>
						)}

						<div className="flex justify-end gap-3">
							<button
								className="rounded-lg border border-gray-600 px-4 py-2 font-medium text-gray-400 text-sm hover:bg-gray-800"
								onClick={() => setShowImportModal(false)}
								type="button"
							>
								Cancel
							</button>
							<button
								className="rounded-lg bg-gray-600 px-4 py-2 font-medium text-sm text-white hover:bg-gray-500 disabled:opacity-50"
								disabled={
									isImporting || (importMode === "csv" ? !importCsv.trim() : !importJson.trim())
								}
								onClick={() => handleImportKaito().catch(() => {})}
								type="button"
							>
								{isImporting ? "Importing..." : "Import"}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Header */}
			<div>
				<h1 className="mb-2 text-balance font-bold text-2xl">Launch Markets</h1>
				<p className="text-gray-400">
					Deploy binary markets for whitelisted creators + custom question.
				</p>
			</div>

			{/* Snapshot Selection */}
			<section className="rounded-lg border border-gray-800 bg-gray-900 p-6">
				<div className="mb-4 flex flex-wrap items-center justify-between gap-4">
					<div>
						<h2 className="font-semibold text-lg">Snapshot Selection</h2>
						<p className="text-gray-500 text-sm">
							Choose a leaderboard snapshot for market creation
						</p>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						<div className="flex rounded-lg border border-gray-700 bg-gray-800 p-0.5">
							{TIME_WINDOWS.map(tw => (
								<button
									className={`rounded-md px-3 py-1.5 text-sm transition ${
										selectedTimeWindow === tw
											? "bg-gray-600 text-white"
											: "text-gray-400 hover:bg-gray-700"
									}`}
									key={tw}
									onClick={() => setSelectedTimeWindow(tw)}
									type="button"
								>
									{formatTimeWindowLabel(tw)}
								</button>
							))}
						</div>
						<button
							className="flex items-center gap-2 rounded-lg border border-gray-600 px-3 py-2 text-gray-300 text-sm hover:bg-gray-800"
							onClick={() => setShowImportModal(true)}
							type="button"
						>
							<Upload aria-hidden="true" className="size-4" />
							Import
						</button>
						<button
							className="flex items-center gap-2 rounded-lg bg-gray-600 px-3 py-2 text-sm text-white hover:bg-gray-500 disabled:opacity-50"
							disabled={isRefreshing}
							onClick={() => handleRefresh().catch(() => {})}
							type="button"
						>
							<RefreshCw
								aria-hidden="true"
								className={cn("size-4", isRefreshing && "animate-spin")}
							/>
							Refresh
						</button>
					</div>
				</div>

				<div className="grid gap-4 md:grid-cols-2">
					<div>
						<label className="mb-1 block text-gray-400 text-sm" htmlFor="snapshot-select">
							Snapshot
						</label>
						<select
							className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white"
							id="snapshot-select"
							onChange={e => setSelectedSnapshotId(e.target.value)}
							value={selectedSnapshotId}
						>
							<option value="">Select a snapshot</option>
							{(snapshots ?? []).map(snapshot => (
								<option key={snapshot.id} value={snapshot.id}>
									{formatDateTimeMs(snapshot.capturedAtMs)} - {snapshot.provider} (
									{snapshot.entityCount} entities){snapshot.ipfsCid ? " ✓" : ""}
								</option>
							))}
						</select>
					</div>

					{selectedSnapshotDetails && (
						<div className="flex items-center gap-6">
							<div>
								<p className="text-gray-500 text-xs">Entities</p>
								<p className="font-medium">{selectedSnapshotDetails.entityCount} creators</p>
							</div>
							<div>
								<p className="text-gray-500 text-xs">IPFS</p>
								{selectedSnapshotDetails.ipfsCid ? (
									<a
										className="font-mono text-green-400 text-sm hover:underline"
										href={`https://gateway.pinata.cloud/ipfs/${selectedSnapshotDetails.ipfsCid}`}
										rel="noopener noreferrer"
										target="_blank"
									>
										{selectedSnapshotDetails.ipfsCid.slice(0, 12)}...
									</a>
								) : (
									<button
										className="rounded bg-gray-700 px-3 py-1 text-sm text-white hover:bg-gray-600 disabled:opacity-50"
										disabled={isUploadingIpfs}
										onClick={() => handleUploadIpfs().catch(() => {})}
										type="button"
									>
										{isUploadingIpfs ? "Uploading..." : "Upload"}
									</button>
								)}
							</div>
						</div>
					)}
				</div>
			</section>

			{/* Leaderboard Preview */}
			{entries.length > 0 && (
				<section className="rounded-lg border border-gray-800 bg-gray-900 p-6">
					<h2 className="mb-4 font-semibold text-lg">Whitelisted Creators ({entries.length})</h2>
					<div className="overflow-x-auto">
						<table className="w-full text-left text-sm">
							<thead>
								<tr className="border-gray-800 border-b text-gray-500">
									<th className="px-3 py-2">Rank</th>
									<th className="px-3 py-2">Creator</th>
									<th className="px-3 py-2">Market Question</th>
									<th className="px-3 py-2 text-right">Options</th>
								</tr>
							</thead>
							<tbody>
								{entries.map(entry => {
									const labels = BINARY_OUTCOME_LABELS
									return (
										<tr
											className="border-gray-800 border-b hover:bg-gray-800/50"
											key={entry.entityKey}
										>
											<td className="px-3 py-2 font-semibold">#{entry.rank}</td>
											<td className="px-3 py-2">
												<div className="flex items-center gap-2">
													{entry.avatarUrl ? (
														<img
															alt=""
															className="size-6 rounded-full object-cover"
															height={24}
															loading="lazy"
															referrerPolicy="no-referrer"
															src={entry.avatarUrl}
															width={24}
														/>
													) : (
														<div className="flex size-6 items-center justify-center rounded-full bg-gray-700 text-xs">
															{entry.displayName.charAt(0)}
														</div>
													)}
													<span>{entry.displayName}</span>
												</div>
											</td>
											<td className="px-3 py-2 text-gray-400">
												{getBinaryMarketTitle(entry.displayName)}
											</td>
											<td className="px-3 py-2 text-right">
												<span className="inline-flex gap-1">
													<span className="rounded bg-green-900/50 px-2 py-0.5 text-green-400 text-xs">
														{labels[0]}
													</span>
													<span className="rounded bg-red-900/50 px-2 py-0.5 text-red-400 text-xs">
														{labels[1]}
													</span>
												</span>
											</td>
										</tr>
									)
								})}
							</tbody>
						</table>
					</div>

					{/* Custom Question Preview */}
					<div className="mt-6 rounded-lg border border-blue-900/50 bg-blue-950/20 p-4">
						<h3 className="mb-2 font-medium text-blue-400">Custom Question Market</h3>
						<p className="text-gray-300 text-sm">{CUSTOM_QUESTION_CONFIG.question}</p>
						<p className="mt-1 text-gray-500 text-xs">
							Binary: {CUSTOM_QUESTION_CONFIG.outcomeLabels[0]} /{" "}
							{CUSTOM_QUESTION_CONFIG.outcomeLabels[1]}
						</p>
					</div>
				</section>
			)}

			{/* Launch Configuration */}
			<section className="rounded-lg border border-gray-800 bg-gray-900 p-6">
				<h2 className="mb-6 font-semibold text-lg">Launch Configuration</h2>

				{/* Market Settings */}
				<div className="mb-6 grid gap-4 sm:grid-cols-2">
					<div>
						<label className="mb-1 block text-gray-400 text-sm" htmlFor="week-id">
							Week ID
						</label>
						<div className="flex gap-2">
							<input
								className="flex-1 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm"
								id="week-id"
								onChange={e => setFormState(p => ({ ...p, weekId: e.target.value }))}
								type="text"
								value={formState.weekId}
							/>
							<button
								aria-label="Generate new Week ID"
								className="rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-gray-400 hover:bg-gray-700"
								onClick={() => setFormState(p => ({ ...p, weekId: generateRandomWeekId() }))}
								type="button"
							>
								<RefreshCw aria-hidden="true" className="size-4" />
							</button>
						</div>
						<p className="mt-1 text-gray-500 text-xs">Unique identifier for this batch</p>
					</div>
					<div>
						<label className="mb-1 block text-gray-400 text-sm" htmlFor="fee-bps">
							Platform Fee
						</label>
						<div className="flex items-center gap-2">
							<input
								className="w-24 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm"
								id="fee-bps"
								min="0"
								onChange={e => setFormState(p => ({ ...p, feeBps: e.target.value }))}
								type="number"
								value={formState.feeBps}
							/>
							<span className="text-gray-400 text-sm">bps</span>
							<span className="rounded bg-gray-800 px-2 py-1 text-gray-300 text-sm">
								= {(Number(formState.feeBps) / 100).toFixed(1)}%
							</span>
						</div>
						<p className="mt-1 text-gray-500 text-xs">Fee on trades (100 bps = 1%)</p>
					</div>
				</div>

				{/* Market Timing */}
				<div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
					<div className="mb-3 flex items-center justify-between">
						<h3 className="font-medium text-gray-300">Market Timing</h3>
						<span className="text-gray-500 text-xs">
							{Intl.DateTimeFormat().resolvedOptions().timeZone}
						</span>
					</div>

					{/* Duration Presets */}
					<div className="mb-4 flex flex-wrap items-center gap-2">
						<span className="text-gray-500 text-sm">Duration:</span>
						{DURATION_PRESETS.map(preset => (
							<button
								className="rounded-md border border-gray-600 px-3 py-1 text-gray-400 text-xs hover:border-gray-500 hover:bg-gray-700 hover:text-white"
								key={preset.label}
								onClick={() => handleDurationPreset(preset.seconds)}
								type="button"
							>
								{preset.label}
							</button>
						))}
					</div>

					<div className="grid gap-4 sm:grid-cols-2">
						<div>
							<label className="mb-1 block text-gray-400 text-sm" htmlFor="open-time">
								Trading Opens
							</label>
							<input
								className="w-full rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-sm"
								id="open-time"
								onChange={e => setFormState(p => ({ ...p, openTimeSec: e.target.value }))}
								type="datetime-local"
								value={formState.openTimeSec}
							/>
						</div>
						<div>
							<label className="mb-1 block text-gray-400 text-sm" htmlFor="cutoff-time">
								Trading Ends
							</label>
							<input
								className="w-full rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-sm"
								id="cutoff-time"
								onChange={e => setFormState(p => ({ ...p, cutoffTimeSec: e.target.value }))}
								type="datetime-local"
								value={formState.cutoffTimeSec}
							/>
						</div>
					</div>

					{/* Duration Display */}
					{tradingDuration.valid ? (
						<div className="mt-3 flex items-center gap-2">
							<div className="h-px flex-1 bg-gray-600" />
							<span className="rounded-full bg-gray-700 px-3 py-1 text-gray-300 text-xs">
								{tradingDuration.label} trading window
							</span>
							<div className="h-px flex-1 bg-gray-600" />
						</div>
					) : (
						<p className="mt-3 text-red-400 text-sm">End time must be after start time</p>
					)}
				</div>

				{/* Liquidity Settings */}
				<div className="mt-6 space-y-4">
					{/* Explanation */}
					<div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
						<h3 className="mb-2 font-medium text-gray-300">How Market Liquidity Works</h3>
						<p className="text-gray-400 text-sm">
							Enter the <span className="font-medium text-white">reserve amount</span> (capital) you
							want to seed each market with. This determines:
						</p>
						<ul className="mt-2 space-y-1 text-gray-500 text-xs">
							<li>
								• <span className="text-gray-400">Trading slippage</span> — More capital = less
								slippage for traders
							</li>
							<li>
								• <span className="text-gray-400">Max payout risk</span> — Reserve covers worst-case
								payouts
							</li>
							<li>
								• <span className="text-gray-400">Seed cost</span> — Custom odds require extra
								reserve
							</li>
						</ul>
					</div>

					<div className="grid gap-4 lg:grid-cols-2">
						{/* Binary Markets */}
						<div className="rounded-lg border border-gray-700 bg-gray-800/30 p-4">
							<h3 className="mb-3 font-medium text-gray-300">Binary Markets ({entries.length})</h3>

							<label className="mb-1 block text-gray-400 text-sm" htmlFor="binary-reserve">
								Reserve per Market
							</label>
							<div className="flex items-center gap-2">
								<span className="text-gray-400 text-lg">$</span>
								<input
									className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 font-medium text-lg"
									id="binary-reserve"
									min="10"
									onChange={e => setFormState(p => ({ ...p, binaryReserveUsd: e.target.value }))}
									step="10"
									type="number"
									value={formState.binaryReserveUsd}
								/>
							</div>

							{/* Calculated values */}
							<div className="mt-3 space-y-2 rounded-lg bg-gray-900/50 p-3">
								<div className="flex justify-between text-sm">
									<span className="text-gray-500">Liquidity param (b):</span>
									<span className="font-mono text-gray-300">${liquidityParams.binary}</span>
								</div>
								<div className="flex justify-between text-sm">
									<span className="text-gray-500">Per market reserve:</span>
									<span className="font-mono text-gray-300">
										{formatUsdc(Number(reserves.binaryPerMarket) / 1e6)}
									</span>
								</div>
								{reserves.binarySeededCount > 0 && (
									<div className="flex justify-between text-sm">
										<span className="text-yellow-500">
											+{reserves.binarySeededCount} seeded market(s):
										</span>
										<span className="font-mono text-yellow-400">
											+{formatUsdc(Number(reserves.binaryExtraReserve) / 1e6)}
										</span>
									</div>
								)}
								<div className="border-gray-700 border-t pt-2">
									<div className="flex justify-between font-medium">
										<span className="text-gray-400">Subtotal ({entries.length} markets):</span>
										<span className="text-white">
											{formatUsdc(Number(reserves.binaryTotal) / 1e6)}
										</span>
									</div>
								</div>
							</div>

							{/* Slippage preview */}
							<div className="mt-3">
								<p className="mb-2 text-gray-500 text-xs">Expected slippage (at 50% odds):</p>
								<div className="flex flex-wrap gap-2">
									{slippagePreview.sampleTrades.map((trade, i) => (
										<div className="rounded bg-gray-800 px-2 py-1 text-center text-xs" key={trade}>
											<div className="text-gray-400">${trade}</div>
											<div
												className={`font-mono ${
													(slippagePreview.binarySlippage[i] ?? 0) > 5
														? "text-red-400"
														: (slippagePreview.binarySlippage[i] ?? 0) > 2
															? "text-yellow-400"
															: "text-green-400"
												}`}
											>
												{(slippagePreview.binarySlippage[i] ?? 0).toFixed(1)}%
											</div>
										</div>
									))}
								</div>
							</div>
						</div>

						{/* Custom Question */}
						<div className="rounded-lg border border-blue-900/50 bg-blue-950/20 p-4">
							<h3 className="mb-3 font-medium text-blue-400">Custom Question (1)</h3>

							<label className="mb-1 block text-gray-400 text-sm" htmlFor="featured-reserve">
								Reserve Amount
							</label>
							<div className="flex items-center gap-2">
								<span className="text-gray-400 text-lg">$</span>
								<input
									className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 font-medium text-lg"
									id="featured-reserve"
									min="10"
									onChange={e => setFormState(p => ({ ...p, featuredReserveUsd: e.target.value }))}
									step="10"
									type="number"
									value={formState.featuredReserveUsd}
								/>
							</div>

							{/* Calculated values */}
							<div className="mt-3 space-y-2 rounded-lg bg-gray-900/50 p-3">
								<div className="flex justify-between text-sm">
									<span className="text-gray-500">Liquidity param (b):</span>
									<span className="font-mono text-gray-300">${liquidityParams.featured}</span>
								</div>
								{reserves.featuredIsSeeded && (
									<div className="flex justify-between text-sm">
										<span className="text-yellow-500">Custom odds extra:</span>
										<span className="font-mono text-yellow-400">
											+{formatUsdc(Number(reserves.featuredExtraReserve) / 1e6)}
										</span>
									</div>
								)}
								<div className="border-gray-700 border-t pt-2">
									<div className="flex justify-between font-medium">
										<span className="text-gray-400">Subtotal:</span>
										<span className="text-blue-300">
											{formatUsdc(Number(reserves.featured) / 1e6)}
										</span>
									</div>
								</div>
							</div>

							{/* Slippage preview */}
							<div className="mt-3">
								<p className="mb-2 text-gray-500 text-xs">Expected slippage (at 50% odds):</p>
								<div className="flex flex-wrap gap-2">
									{slippagePreview.sampleTrades.map((trade, i) => (
										<div className="rounded bg-gray-800 px-2 py-1 text-center text-xs" key={trade}>
											<div className="text-gray-400">${trade}</div>
											<div
												className={`font-mono ${
													(slippagePreview.featuredSlippage[i] ?? 0) > 5
														? "text-red-400"
														: (slippagePreview.featuredSlippage[i] ?? 0) > 2
															? "text-yellow-400"
															: "text-green-400"
												}`}
											>
												{(slippagePreview.featuredSlippage[i] ?? 0).toFixed(1)}%
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Custom Odds (Advanced) */}
				<div className="mt-6 rounded-lg border border-gray-700">
					<button
						className="flex w-full items-center justify-between p-4 text-left hover:bg-gray-800/50"
						onClick={() => setShowOddsConfig(p => !p)}
						type="button"
					>
						<div>
							<h3 className="font-medium text-gray-300">Advanced: Custom Initial Odds</h3>
							<p className="text-gray-500 text-sm">
								Set non-uniform starting odds (default: 50/50)
							</p>
						</div>
						{showOddsConfig ? (
							<ChevronUp aria-hidden="true" className="size-5 text-gray-400" />
						) : (
							<ChevronDown aria-hidden="true" className="size-5 text-gray-400" />
						)}
					</button>

					{showOddsConfig && (
						<div className="border-gray-700 border-t p-4">
							{/* Binary Markets Odds */}
							<div className="mb-4">
								<h4 className="mb-2 font-medium text-gray-400 text-sm">Binary Markets</h4>
								<p className="mb-3 text-gray-500 text-xs">
									Set Long/Short odds. Leave empty for 50/50.
								</p>
								<div className="max-h-64 space-y-2 overflow-y-auto">
									{entries.map((entry, index) => {
										const customOdds = formState.binaryOdds.get(index)
										const hasCustom = customOdds !== undefined
										return (
											<div
												className="flex items-center gap-3 rounded-lg bg-gray-800/50 p-2"
												key={entry.entityKey}
											>
												<span className="w-8 text-gray-500 text-xs">#{entry.rank}</span>
												<span className="w-32 truncate text-sm">{entry.displayName}</span>
												<div className="flex items-center gap-2">
													<input
														aria-label={`Yes % for ${entry.displayName}`}
														className="w-16 rounded border border-gray-600 bg-gray-700 px-2 py-1 text-center text-sm"
														onChange={e => {
															const yesVal = Number.parseInt(e.target.value, 10) || 50
															const noVal = 100 - yesVal
															setFormState(p => {
																const newOdds = new Map(p.binaryOdds)
																if (yesVal === 50) {
																	newOdds.delete(index)
																} else {
																	newOdds.set(index, [yesVal, noVal])
																}
																return { ...p, binaryOdds: newOdds }
															})
														}}
														placeholder="50"
														type="number"
														value={hasCustom ? customOdds[0] : ""}
													/>
													<span className="text-gray-500 text-xs">% Yes</span>
												</div>
												{hasCustom && (
													<>
														<span className="text-gray-400 text-xs">({customOdds[1]}% No)</span>
														<button
															aria-label={`Reset ${entry.displayName}`}
															className="text-gray-400 hover:text-red-400"
															onClick={() =>
																setFormState(p => {
																	const newOdds = new Map(p.binaryOdds)
																	newOdds.delete(index)
																	return { ...p, binaryOdds: newOdds }
																})
															}
															type="button"
														>
															<X aria-hidden="true" className="size-4" />
														</button>
													</>
												)}
											</div>
										)
									})}
								</div>
								{formState.binaryOdds.size > 0 && (
									<p className="mt-2 text-gray-400 text-xs">
										{formState.binaryOdds.size} market(s) with custom odds
									</p>
								)}
							</div>

							{/* Custom Question Odds */}
							<div>
								<h4 className="mb-2 font-medium text-blue-400 text-sm">Custom Question</h4>
								<p className="mb-3 text-gray-500 text-xs">
									Set odds for 2 outcomes. Must sum to 100%. Empty = 50/50.
								</p>
								<div className="flex flex-wrap gap-3">
									{CUSTOM_QUESTION_CONFIG.outcomeLabels.map((label, i) => {
										const currentOdds = formState.featuredOdds?.[i] ?? ""
										return (
											<div className="flex items-center gap-2" key={label}>
												<span className="w-24 truncate text-gray-400 text-xs">{label}</span>
												<input
													aria-label={`Odds for ${label}`}
													className="w-16 rounded border border-gray-600 bg-gray-700 px-2 py-1 text-center text-sm"
													onChange={e => {
														const val = Number.parseInt(e.target.value, 10) || 0
														setFormState(p => {
															const newOdds = p.featuredOdds ? [...p.featuredOdds] : [50, 50]
															newOdds[i] = val
															if (newOdds.every(o => o === 50)) {
																return { ...p, featuredOdds: undefined }
															}
															return { ...p, featuredOdds: newOdds }
														})
													}}
													placeholder="50"
													type="number"
													value={currentOdds}
												/>
												<span className="text-gray-500 text-xs">%</span>
											</div>
										)
									})}
								</div>
								{formState.featuredOdds && (
									<div className="mt-2">
										{(() => {
											const sum = formState.featuredOdds.reduce((a, b) => a + b, 0)
											const validation = validateOdds(formState.featuredOdds)
											if (!validation.valid) {
												return <p className="text-red-400 text-xs">{validation.error}</p>
											}
											return <p className="text-blue-400 text-xs">Total: {sum}% ✓</p>
										})()}
										<button
											className="mt-1 text-gray-400 text-xs hover:text-red-400"
											onClick={() => setFormState(p => ({ ...p, featuredOdds: undefined }))}
											type="button"
										>
											Reset to uniform
										</button>
									</div>
								)}
							</div>
						</div>
					)}
				</div>

				{/* Total and Launch Button */}
				<div className="mt-6 space-y-3">
					{/* Odds Validation Warning */}
					{!oddsValidation.valid && (
						<div className="rounded-lg border border-yellow-800 bg-yellow-950/50 p-4">
							<p className="mb-2 font-medium text-yellow-400">Custom Odds Validation Errors</p>
							<ul className="space-y-1 text-sm text-yellow-400/80">
								{oddsValidation.errors.map(error => (
									<li key={error}>• {error}</li>
								))}
							</ul>
						</div>
					)}

					{/* Balance Warning */}
					{!balanceInfo.isLoading && !balanceInfo.hasSufficientBalance && (
						<div className="flex items-center justify-between rounded-lg border border-red-800 bg-red-950/50 p-4">
							<div className="flex items-center gap-3">
								<div className="flex size-10 items-center justify-center rounded-full bg-red-900/50">
									<span className="text-red-400 text-xl">!</span>
								</div>
								<div>
									<p className="font-medium text-red-400">Insufficient USDC Balance</p>
									<p className="text-red-400/80 text-sm">
										You need {formatUsdc(Number(balanceInfo.shortfall) / 1e6)} more USDC to launch
									</p>
								</div>
							</div>
							<button
								className="rounded-lg border border-red-700 px-3 py-1.5 text-red-400 text-sm hover:bg-red-900/30"
								onClick={() => refetchBalance().catch(() => {})}
								type="button"
							>
								Refresh Balance
							</button>
						</div>
					)}

					<div className="flex items-center justify-between rounded-lg border border-gray-700 bg-gray-800 p-4">
						<div>
							<p className="text-gray-500 text-sm">Total USDC Required</p>
							<p className="font-bold text-2xl">{formatUsdc(Number(reserves.total) / 1e6)}</p>
							{!balanceInfo.isLoading && (
								<p
									className={cn(
										"text-sm",
										balanceInfo.hasSufficientBalance ? "text-green-400" : "text-red-400",
									)}
								>
									Your balance: {formatUsdc(Number(balanceInfo.balance) / 1e6)} USDC
									{balanceInfo.hasSufficientBalance ? " ✓" : ""}
								</p>
							)}
						</div>
						<button
							className="rounded-lg bg-green-600 px-6 py-3 font-semibold text-white hover:bg-green-500 disabled:cursor-not-allowed disabled:opacity-50"
							disabled={
								!address ||
								isLaunching ||
								!selectedSnapshotId ||
								entries.length < 1 ||
								!balanceInfo.hasSufficientBalance ||
								!oddsValidation.valid
							}
							onClick={() => handleLaunch().catch(() => {})}
							title={
								!selectedSnapshotId
									? "Select a snapshot first"
									: !balanceInfo.hasSufficientBalance
										? "Insufficient USDC balance"
										: !oddsValidation.valid
											? "Fix odds validation errors first"
											: undefined
							}
							type="button"
						>
							{isLaunching
								? launchProgress || "Launching..."
								: `Launch ${entries.length + 1} Markets${!selectedSnapshotDetails?.ipfsCid ? " (will upload to IPFS)" : ""}`}
						</button>
					</div>
				</div>
			</section>
		</div>
	)
}
